const archiveNames = new Set(['GAME.DAT', 'EPISODE_I.DAT', 'EPISODE_II.DAT', 'EPISODE_III.DAT', 'EPISODE_IV.DAT', 'EPISODE_V.DAT', 'EPISODE_VI.DAT']);
const maximumArchiveSize = 2 * 1024 * 1024 * 1024;

function validArchive(entry) {
  return entry && typeof entry.name === 'string' && archiveNames.has(entry.name.toUpperCase()) &&
    Number.isSafeInteger(entry.size) && entry.size >= 16 && entry.size <= maximumArchiveSize;
}

export function validateHostedManifest(value) {
  if (!value || !Array.isArray(value.archives) || value.archives.length !== archiveNames.size) throw new Error('Hosted archive manifest is incomplete.');
  const seen = new Set();
  const archives = value.archives.map(entry => ({ name: String(entry?.name ?? '').toUpperCase(), size: entry?.size }));
  for (const entry of archives) {
    if (!validArchive(entry) || seen.has(entry.name)) throw new Error('Hosted archive manifest is invalid.');
    seen.add(entry.name);
  }
  return archives.sort((a, b) => a.name.localeCompare(b.name));
}

function rangeBlob(name, size, endpoint, request) {
  return { size, async arrayBuffer() {
    const response = await request(endpoint, { headers: { Range: `bytes=${this.start}-${this.end - 1}` }, credentials: 'same-origin' });
    if (response.status !== 206) throw new Error(`Hosted ${name} range request failed (${response.status}).`);
    const expected = `bytes ${this.start}-${this.end - 1}/${size}`;
    if (response.headers.get('Content-Range') !== expected) throw new Error(`Hosted ${name} returned an invalid byte range.`);
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength !== this.end - this.start) throw new Error(`Hosted ${name} returned an incomplete byte range.`);
    return bytes;
  }, start: 0, end: size };
}

export function createRemoteArchiveFiles(manifest, { endpoint = '/game-assets', request = fetch } = {}) {
  const archives = validateHostedManifest(manifest);
  if (typeof endpoint !== 'string' || !endpoint.startsWith('/') || endpoint.includes('?') || typeof request !== 'function') throw new Error('Invalid hosted archive configuration.');
  return archives.map(({ name, size }) => ({ name, size, webkitRelativePath: name,
    slice(start = 0, end = size) {
      const from = Math.max(0, Math.min(size, Number(start))), to = Math.max(from, Math.min(size, Number(end)));
      if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to)) throw new Error('Invalid hosted archive byte range.');
      const part = rangeBlob(name, size, `${endpoint}/${encodeURIComponent(name)}`, request);
      part.start = from; part.end = to;
      return part;
    },
  }));
}

export async function loadHostedArchiveFiles({ endpoint = '/game-assets', request = fetch } = {}) {
  const response = await request(`${endpoint}/manifest`, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('Hosted game archives are unavailable.');
  return createRemoteArchiveFiles(await response.json(), { endpoint, request });
}
